import { Controller, useForm } from "react-hook-form";
import { useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { adaptBatchItem } from "@/features/cosignature/helpers/adapters";
import type { BatchCosignItem } from "@/features/cosignature/types";
import { Button2 } from "@/components/UI2/atoms/Button";
import { Checkbox2 } from "@/components/UI2/atoms/Checkbox";
import { COSIGNATURE_ACKNOWLEDGEMENT } from "@/features/cosignature/constants";
import { FieldError2 } from "@/components/UI2/atoms/FieldError";
import { ModalControlled2, ModalDivider } from "@/components/UI2/molecules/Modal";
import { useBatchCosignDocuments } from "@/features/cosignature/api/mutations/useBatchCosignDocuments";

import { batchCosignSchema, type BatchCosignFormValues } from "./modal.schemas";

type BatchCosignNotesModalProps = {
  isOpen: boolean;
  items: BatchCosignItem[];
  onClose: () => void;
  onSuccess?: VoidFunction;
};

export function BatchCosignNotesModal({
  isOpen,
  items,
  onClose,
  onSuccess,
}: BatchCosignNotesModalProps) {
  const { control, formState, handleSubmit, reset } = useForm<BatchCosignFormValues>({
    defaultValues: { isConfirmed: false },
    mode: "onChange",
    resolver: zodResolver(batchCosignSchema),
  });

  const { isPending, mutate } = useBatchCosignDocuments({
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const onSubmit = useCallback(
    () =>
      mutate({
        items: adaptBatchItem(items),
      }),
    [items, mutate],
  );

  return (
    <ModalControlled2 isOpen={isOpen} onOpenChange={handleClose} title="Approve & Co-Sign">
      <form className="tw:flex tw:flex-col tw:gap-4" onSubmit={handleSubmit(onSubmit)}>
        <p className="tw:text-text-secondary">
          You are about to co-sign{" "}
          <b>
            {items.length} {items.length === 1 ? "note" : "notes"}
          </b>
          . This action is final and will mark {items.length === 1 ? "this note" : "these notes"} as
          billable.
        </p>

        <Controller
          control={control}
          name="isConfirmed"
          render={({ field, fieldState }) => (
            <div className="tw:rounded-sm tw:border tw:border-stroke-tertiary tw:px-3 tw:py-2">
              <Checkbox2
                className="tw:text-text-secondary"
                isInvalid={!!fieldState.error}
                isSelected={field.value}
                onChange={field.onChange}
              >
                {COSIGNATURE_ACKNOWLEDGEMENT}
              </Checkbox2>
              {fieldState.error && <FieldError2>{fieldState.error.message}</FieldError2>}
            </div>
          )}
        />

        <ModalDivider />

        <div className="tw:flex tw:justify-end tw:gap-3">
          <Button2 onPress={handleClose} variant="secondary">
            Cancel
          </Button2>

          <Button2
            isDisabled={!formState.isValid}
            isLoading={isPending}
            type="submit"
            variant="primary"
          >
            Approve &amp; Co-Sign
          </Button2>
        </div>
      </form>
    </ModalControlled2>
  );
}
